import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MODULES } from 'src/app/core/config/module-codes';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { BankAccountVisibilityService } from 'src/app/data/service/bank-account-visibility.service';
import { UpgradeModalService } from 'src/app/shared/billing/upgrade-modal/upgrade-modal.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { DailySyncAccount, DailySyncModalComponent, DailySyncModalResult } from './daily-sync-modal.component';

/**
 * Opens the daily sync dialog from wherever it is offered (the Import from Xero step, the
 * Transactions account menu), with Inbox Automator's upgrade loop: "Upgrade" swaps to the
 * upgrade modal and back. Saving fires TransactionDailySyncService.changed$, so callers reload.
 *
 * It offers the accounts the Transactions screen shows: one hidden there is left out here too.
 */
@Injectable({ providedIn: 'root' })
export class DailySyncDialogService {

  constructor(
    private _modal: NgbModal,
    private _upgradeModal: UpgradeModalService,
    private _toaster: ToasterMessageService,
    private _visibility: BankAccountVisibilityService
  ) { }

  async open(organisationId: string, accounts: DailySyncAccount[]): Promise<DailySyncModalResult> {
    // Failing to read the hidden ones offers every account, as the Transactions screen does.
    const hidden = new Set((await this._visibility.hiddenAccountIds(organisationId)
      .pipe(catchError(() => of([] as string[]))).toPromise() || []).map(id => (id || '').toLowerCase()));
    accounts = accounts.filter(a => !hidden.has((a.accountID || '').toLowerCase()));

    let justUpgraded = false;
    for (;;) {
      const ref = this._modal.open(DailySyncModalComponent, {
        centered: true,
        windowClass: 'synced-dialog synced-dialog--form synced-dialog--elevated',
        backdropClass: 'synced-dialog-backdrop synced-dialog-backdrop--elevated'
      });
      ref.componentInstance.options = { organisationId, accounts, justUpgraded };
      const result: DailySyncModalResult = await ref.result.then(r => r || null, () => null);
      if (result !== 'upgrade') {
        if (result === 'activated') {
          this._toaster.displayToasterState(ToasterTypes.Success, 'Daily sync on',
            'New transactions will arrive every morning at 10:00.');
        } else if (result === 'deactivated') {
          this._toaster.displayToasterState(ToasterTypes.Success, 'Daily sync off', 'Transactions are no longer synced automatically.');
        } else if (result === 'saved') {
          this._toaster.displayToasterState(ToasterTypes.Success, 'Daily sync saved', 'Your changes apply from the next run.');
        }
        return result;
      }
      justUpgraded = await this._upgradeModal.open({
        reason: 'transaction-daily-sync',
        requiredFeature: MODULES.TRANSACTION_DAILY_SYNC,
        featureName: 'Daily transaction sync'
      });
    }
  }
}
